'use client'

import {
  ANALYTICS_EVENTS,
  ANALYTICS_READY_EVENT,
  isAnalyticsDispatchReady,
  trackEvent,
} from '@/lib/analytics'

type Exposure = {
  experimentId: string
  variationId: string
}

const pendingExposures = new Map<string, Exposure>()
const sentExposureKeys = new Set<string>()
let isListeningForAnalytics = false

const exposureKey = ({ experimentId, variationId }: Exposure): string =>
  `${experimentId}:${variationId}`

function dispatchExposure(exposure: Exposure): void {
  const key = exposureKey(exposure)
  if (sentExposureKeys.has(key)) return

  sentExposureKeys.add(key)
  trackEvent(ANALYTICS_EVENTS.experimentViewed, {
    experiment_id: exposure.experimentId,
    variation_id: exposure.variationId,
  })
}

function flushPendingExposures(): void {
  if (!isAnalyticsDispatchReady()) return

  for (const [key, exposure] of pendingExposures) {
    pendingExposures.delete(key)
    dispatchExposure(exposure)
  }

  if (pendingExposures.size === 0 && isListeningForAnalytics) {
    window.removeEventListener(ANALYTICS_READY_EVENT, flushPendingExposures)
    isListeningForAnalytics = false
  }
}

/**
 * Report one experiment exposure after every configured analytics queue is
 * installed. The hero can enter the viewport before Next.js runs its
 * `afterInteractive` GA/Pixel snippets; retaining this small in-memory queue
 * preserves the experiment denominator without ever double-counting it.
 */
export function trackExperimentExposure(experimentId: string, variationId: string): void {
  const exposure = { experimentId, variationId }
  const key = exposureKey(exposure)
  if (sentExposureKeys.has(key) || pendingExposures.has(key)) return

  pendingExposures.set(key, exposure)
  flushPendingExposures()

  if (pendingExposures.size > 0 && !isListeningForAnalytics) {
    window.addEventListener(ANALYTICS_READY_EVENT, flushPendingExposures)
    isListeningForAnalytics = true
  }
}

export { ANALYTICS_READY_EVENT }
